import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TablePagination,
  IconButton,
  TextField,
  MenuItem,
  Grid,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useGetStockTransfersQuery, useGetLocationsQuery, useDeleteStockTransferMutation } from '../../../store/api/inventoryApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function StockTransfersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');

  const { data: transfersData, isLoading } = useGetStockTransfersQuery({
    page,
    limit,
    fromLocationId: fromLocation || undefined,
    toLocationId: toLocation || undefined,
  });

  const { data: locations } = useGetLocationsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const [deleteStockTransfer] = useDeleteStockTransferMutation();
  const currency = company?.currency || 'USD';

  const handleDelete = async (id, transferNo) => {
    if (window.confirm(`Are you sure you want to delete stock transfer ${transferNo}? This will reverse the stock and return items back to the source location.`)) {
      try {
        await deleteStockTransfer(id).unwrap();
        alert('Stock transfer deleted and items returned to source successfully.');
      } catch (err) {
        console.error(err);
        alert(err?.data?.message || 'Failed to delete stock transfer');
      }
    }
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: company?.currencyDecimals ?? 2,
    }).format(amount);
  };

  const transfers = transfersData?.transfers || [];
  const pagination = transfersData?.pagination;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Stock Transfers
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/inventory/transfers/new')}
        >
          New Transfer
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="From Location"
                value={fromLocation}
                onChange={(e) => setFromLocation(e.target.value)}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="To Location"
                value={toLocation}
                onChange={(e) => setToLocation(e.target.value)}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Transfer No</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>From Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>To Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Value</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
              ) : transfers.length > 0 ? (
                transfers.map((trf) => (
                  <TableRow key={trf.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{trf.transferNo}</TableCell>
                    <TableCell>{new Date(trf.transferDate).toLocaleDateString()}</TableCell>
                    <TableCell>{trf.fromLocation?.name}</TableCell>
                    <TableCell>{trf.toLocation?.name}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatAmount(trf.totalAmount)}</TableCell>
                    <TableCell>
                      <Chip
                        label={trf.status}
                        color={trf.status === 'completed' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => navigate(`/inventory/transfers/${trf.id}`)} color="primary">
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(trf.id, trf.transferNo)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">No transfers found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination && (
          <TablePagination
            component="div"
            count={pagination.total}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => setLimit(parseInt(e.target.value, 10))}
          />
        )}
      </Card>
    </Box>
  );
}

export default StockTransfersList;
